import API from "./api";

const BatchService = {
  // Get all batches
  getAllBatches: async () => {
    try {
      const response = await API.get("/batches");
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single batch with students
  getBatchById: async (id) => {
    try {
      const response = await API.get(`/batches/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new batch
  createBatch: async (batchData) => {
    try {
      const response = await API.post("/batches", batchData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update batch
  updateBatch: async (id, batchData) => {
    try {
      const response = await API.put(`/batches/${id}`, batchData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete batch
  deleteBatch: async (id) => {
    try {
      const response = await API.delete(`/batches/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add student to batch
  addStudentToBatch: async (batchId, studentId, joinedDate = null) => {
    try {
      const data = { student_id: studentId };
      if (joinedDate) {
        data.joined_date = joinedDate;
      }
      const response = await API.post(`/batches/${batchId}/students`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Remove student from batch
  removeStudentFromBatch: async (batchId, studentId) => {
    try {
      const response = await API.delete(
        `/batches/${batchId}/students/${studentId}`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update student progress
  updateStudentProgress: async (batchId, studentId, progress) => {
    try {
      const response = await API.put(
        `/batches/${batchId}/students/${studentId}`,
        { progress },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get students ready for certificate
  getReadyStudents: async (batchId) => {
    try {
      const response = await API.get(`/batches/${batchId}/ready-certificates`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get course materials with enabled status for a batch
  getBatchCourseMaterials: async (batchId) => {
    try {
      console.log("Fetching course materials for batch:", batchId);
      const response = await API.get(`/batches/${batchId}/course-materials`);
      console.log("Materials response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching materials:", error);
      throw error;
    }
  },

  // Toggle a single course material visibility
  toggleCourseMaterial: async (batchId, materialId) => {
    try {
      console.log("Toggling material:", materialId, "for batch:", batchId);
      const response = await API.post(
        `/batches/${batchId}/toggle-material/${materialId}`,
      );
      console.log("Toggle response:", response.data);
      return response.data;
    } catch (error) {
      console.error("Toggle error:", error);
      throw error;
    }
  },

  // Bulk enable multiple course materials
  bulkEnableMaterials: async (batchId, materialIds) => {
    try {
      const response = await API.post(`/batches/${batchId}/bulk-enable`, {
        material_ids: materialIds,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateAutoProgress: async (batchId) => {
    try {
      const response = await API.get(`/batches/${batchId}/auto-progress`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update student progress manually (admin)
  updateStudentProgressManual: async (batchId, studentId, increment) => {
    try {
      const response = await API.put(
        `/batches/${batchId}/students-manual/${studentId}`,
        {
          increment: increment,
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Recalculate progress for all students in batch
  recalculateProgress: async (batchId) => {
    try {
      const response = await API.post(
        `/batches/${batchId}/recalculate-progress`,
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Export batch students
  exportBatchStudents: async (batchId) => {
    try {
      const params = new URLSearchParams();
      params.append("type", "batch-students");
      params.append("batch_id", batchId);

      const response = await API.get(`/export?${params.toString()}`, {
        responseType: "blob",
      });

      // Get filename from header or use default
      const contentDisposition = response.headers["content-disposition"];
      let filename = `batch_${batchId}_students.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      throw error;
    }
  },

  // Get students in a batch (simple list for test assignment) - FIXED FOR LIVE SERVER
  getBatchStudents: async (batchId) => {
    try {
      const response = await API.get(`/batches/${batchId}/students`);

      // Normalize response to always return an object with data array
      let students = [];

      if (response.data && Array.isArray(response.data)) {
        students = response.data;
      } else if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        students = response.data.data;
      } else if (
        response.data &&
        response.data.data &&
        response.data.data.students &&
        Array.isArray(response.data.data.students)
      ) {
        students = response.data.data.students;
      } else if (Array.isArray(response.data)) {
        students = response.data;
      }

      // Return in the format CreateTest expects
      return { data: students };
    } catch (error) {
      console.error("Error fetching batch students:", error);
      throw error;
    }
  },
};

export default BatchService;
